import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Paper,
    Slide,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const AuditLogModal = ({ open, handleClose, user }) => {
    if (!user) return null;

    // Sample audit logs for demo
    const auditLogs = [
        {
            id: 1,
            date: '24/01/2026 10:30 AM',
            action: 'Login',
            details: 'User logged in from IP 192.168.1.1',
            status: 'Success'
        },
        {
            id: 2,
            date: '23/01/2026 02:15 PM',
            action: 'Profile Updated',
            details: 'Email address changed',
            status: 'Success'
        },
        {
            id: 3,
            date: '22/01/2026 11:45 AM',
            action: 'Password Changed',
            details: 'Password reset successful',
            status: 'Success'
        },
        {
            id: 4,
            date: '21/01/2026 05:20 PM',
            action: 'Failed Login',
            details: 'Invalid password attempt from IP 192.168.1.5',
            status: 'Failed'
        },
        {
            id: 5,
            date: '20/01/2026 09:00 AM',
            action: 'Account Status Changed',
            details: 'Account status changed from Inactive to Active',
            status: 'Success'
        },
    ];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '90vh',
                    overflow: 'hidden'
                }
            }}
        >
            {/* Header with Gradient Background - Matching SocietyDetailsModal */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #9333ea 0%, #a855f7 100%)',
                    color: 'white',
                    position: 'relative',
                }}
            >
                {/* Background Pattern */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                />

                <DialogTitle sx={{ p: 2.5, pb: 1, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Icon icon="mdi:history" width="28" height="28" />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                                    Audit Logs
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.25 }}>
                                    User: {user.name}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
            </Box>

            {/* Content */}
            <DialogContent sx={{ p: 2.5, maxHeight: 'calc(90vh - 140px)', overflow: 'auto', bgcolor: '#f8fafc' }}>
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f3f4f6', '& .MuiTableCell-head': { fontWeight: 600, fontSize: '0.875rem' } }}>
                            <TableRow>
                                <TableCell>Date & Time</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Details</TableCell>
                                <TableCell align="center">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {auditLogs.map((log) => (
                                <TableRow key={log.id} hover>
                                    <TableCell sx={{ fontSize: '0.875rem' }}>{log.date}</TableCell>
                                    <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{log.action}</TableCell>
                                    <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>{log.details}</TableCell>
                                    <TableCell align="center">
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1,
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                backgroundColor: log.status === 'Success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                                color: log.status === 'Success' ? '#22c55e' : '#ef4444'
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    backgroundColor: log.status === 'Success' ? '#22c55e' : '#ef4444'
                                                }}
                                            />
                                            {log.status}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Summary Stats */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#f3f4f6', borderRadius: 2, display: 'flex', gap: 3 }}>
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            Total Activities
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {auditLogs.length}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            Successful
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#22c55e' }}>
                            {auditLogs.filter(l => l.status === 'Success').length}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            Failed
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>
                            {auditLogs.filter(l => l.status === 'Failed').length}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default AuditLogModal;
